import { useEffect } from "react";

// Composant SEO réutilisable (sans dépendance) : met à jour le <title> et les balises
// meta de la page courante (description, canonical, robots, Open Graph, Twitter Card,
// JSON-LD). Les moteurs qui exécutent le JS les lisent au chargement ; le pré-rendu
// (build) les fige dans le HTML servi aux robots/aperçus sociaux qui n'exécutent pas JS.
// Une seule instance <Seo/> par page. Réutiliser au lieu de dupliquer du code meta.

const SITE_NAME = "AfriFuté Atlas";
const SITE_URL = (process.env.REACT_APP_SITE_URL
  || (typeof window !== "undefined" ? window.location.origin : "")).replace(/\/$/, "");
const DEFAULT_IMAGE = SITE_URL + "/afrifute-logo.png";

function upsertMeta(attr, key, content) {
  if (content == null || content === "") return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", String(content));
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function Seo({ title, description, image, path, type = "website", noindex = false, jsonLd }) {
  const fullTitle = title ? `${title} · ${SITE_NAME}` : SITE_NAME;
  const pathname = path || (typeof window !== "undefined" ? window.location.pathname : "/");
  const url = SITE_URL + pathname;
  const img = image ? (/^https?:/.test(image) ? image : SITE_URL + image) : DEFAULT_IMAGE;
  const ldStr = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    document.title = fullTitle;
    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
    upsertLink("canonical", url);

    // Open Graph (Facebook, LinkedIn, WhatsApp…)
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:locale", "fr_FR");
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", img);

    // Twitter Card
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", img);

    // JSON-LD (données structurées)
    let ld = document.getElementById("seo-jsonld");
    if (ldStr) {
      if (!ld) {
        ld = document.createElement("script");
        ld.id = "seo-jsonld";
        ld.type = "application/ld+json";
        document.head.appendChild(ld);
      }
      ld.textContent = ldStr;
    } else if (ld) {
      ld.textContent = "";
    }
  }, [fullTitle, description, url, img, type, noindex, ldStr]);

  return null;
}
